import { Group, Quaternion, Vector3 } from 'three/webgpu';
import { LayerRuntime } from './layer.js';
import { deriveSeed } from './math/prng.js';
/**
 * A playing (or pooled-idle) copy of an effect. Owns one LayerRuntime per
 * layer; reused across spawns without reallocation (plan §1.4: pooling is not
 * optional).
 */
export class EffectInstance {
    root = new Group();
    time = 0;
    def;
    layers;
    seed = 1;
    emitting = true;
    active = false;
    frame = {
        position: new Vector3(),
        quaternion: new Quaternion(),
        scale: 1,
        velocity: new Vector3(),
    };
    pendingMove = null;
    moveTarget = new Vector3();
    /**
     * Sub-emitter plumbing: event buffers per source layer (reused, cleared each
     * frame) and which layers listen to which source (child index lists).
     */
    events = [];
    wantsEvents = [];
    triggerChildren = [];
    constructor(def, seed) {
        this.def = def;
        this.layers = def.layers.map((l, i) => new LayerRuntime(l, def.meta, i, deriveSeed(seed, i)));
        for (const l of this.layers)
            this.root.add(l.object);
        this.root.visible = false;
        this.rebuildTriggerIndex();
    }
    rebuildTriggerIndex() {
        this.events = this.def.layers.map(() => ({ births: [], deaths: [] }));
        this.wantsEvents = this.def.layers.map(() => false);
        this.triggerChildren = [];
        this.def.layers.forEach((layer, child) => {
            const trigger = layer.emitter.trigger;
            if (!trigger)
                return;
            const parent = this.def.layers.findIndex((l) => l.id === trigger.parent);
            if (parent < 0 || parent === child)
                return;
            this.wantsEvents[parent] = true;
            this.triggerChildren.push({ child, parent, on: trigger.on });
        });
    }
    /** (Re)start this instance at a transform. Called by EffectAsset.spawn(). */
    activate(opts, seed) {
        this.seed = seed;
        this.time = 0;
        this.emitting = true;
        this.active = true;
        this.root.visible = true;
        this.pendingMove = null;
        this.frame.velocity.set(0, 0, 0);
        const p = opts.position;
        if (p instanceof Vector3)
            this.frame.position.copy(p);
        else if (p)
            this.frame.position.set(p[0], p[1], p[2]);
        else
            this.frame.position.set(0, 0, 0);
        if (opts.quaternion)
            this.frame.quaternion.copy(opts.quaternion);
        else
            this.frame.quaternion.identity();
        this.frame.scale = opts.scale ?? 1;
        if (this.def.meta.simulationSpace === 'local') {
            // Particles ride the root; moving root.position moves the whole effect.
            this.root.position.copy(this.frame.position);
            this.root.quaternion.copy(this.frame.quaternion);
        }
        else {
            // World space: transform is baked into particles at birth instead.
            this.root.position.set(0, 0, 0);
            this.root.quaternion.identity();
        }
        this.layers.forEach((l, i) => l.reset(deriveSeed(seed, i)));
    }
    /**
     * Move a live instance (dust behind a runner). In 'world' sim space
     * particles stay behind; the InheritVelocity module reads the implied
     * velocity at each birth.
     */
    move(position) {
        if (position instanceof Vector3)
            this.moveTarget.copy(position);
        else
            this.moveTarget.set(position[0], position[1], position[2]);
        this.pendingMove = this.moveTarget;
    }
    update(dt) {
        if (!this.active)
            return;
        if (this.pendingMove && dt > 0) {
            this.frame.velocity.copy(this.pendingMove).sub(this.frame.position).divideScalar(dt);
            this.frame.position.copy(this.pendingMove);
            if (this.def.meta.simulationSpace === 'local')
                this.root.position.copy(this.frame.position);
            this.pendingMove = null;
        }
        else if (dt > 0) {
            this.frame.velocity.set(0, 0, 0);
        }
        const t0 = this.time;
        this.time = t0 + dt;
        // pass 1: simulate every layer, collecting birth/death events where needed
        for (let i = 0; i < this.layers.length; i++) {
            const ev = this.events[i];
            ev.births.length = 0;
            ev.deaths.length = 0;
            this.layers[i].update(t0, this.time, this.emitting, this.frame, this.wantsEvents[i] ? ev : undefined);
        }
        // pass 2: sub-emitters consume parent events (one level deep by design)
        for (const t of this.triggerChildren) {
            const source = this.events[t.parent];
            const positions = t.on === 'birth' ? source.births : source.deaths;
            const child = this.layers[t.child];
            for (let k = 0; k < positions.length; k += 3) {
                child.spawnTriggered(positions[k], positions[k + 1], positions[k + 2], this.time, this.frame);
            }
        }
    }
    /** Stop emission; live particles finish naturally (plan §1.4 API). */
    stop() {
        this.emitting = false;
    }
    get isEmitting() {
        return this.emitting;
    }
    /** Read-only access to per-layer runtimes (stats, tests, tooling). */
    get layerRuntimes() {
        return this.layers;
    }
    get aliveParticles() {
        let n = 0;
        for (const l of this.layers)
            n += l.aliveCount;
        return n;
    }
    /** True once playback is over and every particle has died — ready to recycle. */
    get finished() {
        if (!this.active)
            return true;
        const emissionOver = this.def.meta.loop ? !this.emitting : this.time >= this.def.meta.duration || !this.emitting;
        return emissionOver && this.aliveParticles === 0 && this.time > 0;
    }
    /** Rewind to t=0 with the same seed — identical replay (determinism convention). */
    resetPlayback() {
        this.time = 0;
        this.emitting = true;
        this.layers.forEach((l, i) => l.reset(deriveSeed(this.seed, i)));
    }
    /**
     * Deterministic scrub: re-simulate from 0 to `t` in fixed steps. Used by the
     * studio's scrub bar; CPU sim at a few hundred particles makes this cheap.
     */
    scrubTo(t, step = 1 / 120) {
        this.resetPlayback();
        let remaining = Math.max(0, t);
        while (remaining > 1e-9) {
            const d = Math.min(step, remaining);
            this.update(d);
            remaining -= d;
        }
    }
    /** Live-edit path: def with unchanged structure (see EffectAsset.updateDef). */
    applyDef(def) {
        this.def = def;
        this.layers.forEach((l, i) => l.applyDef(def.layers[i], def.meta));
        this.rebuildTriggerIndex();
    }
    /** Hard-stop and hide; the owning asset returns it to the pool. */
    deactivate() {
        this.active = false;
        this.emitting = false;
        this.root.visible = false;
    }
    get isActive() {
        return this.active;
    }
    dispose() {
        for (const l of this.layers)
            l.dispose();
        this.root.removeFromParent();
    }
}
